import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import type { AppDispatch } from '/business/shared/state/store';
import type {
  ElectricityHeatSteamConsumptionData,
  ElectricityHeatSteamLocationDecorated,
} from '/business/shared/state/selectors/application/electricityHeatSteam/electHeatSteamTypes';

import formatTonnage from '/shared/functions/formatTonnage';

import { urlApplicationRoot } from '/business/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';
import getElectDecoratedLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectDecoratedLocationsSelector';
import StyledHeading from '/shared/styled/components/StyledHeading';
import getElectNumLocationsInputtedSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsInputtedSelector';
import StyledTabBar, { StyledTabBarItem } from './__styled__/StyledTabBar';
import LinkButton from '/shared/components/widgets/button/LinkButton';
import Button from '/shared/components/widgets/button/Button';
import saveConsumptionData from '/business/shared/state/actions/basket/package2/saveConsumptionData';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import StyledBasketTable from '/business/shared/components/widgets/basket-table/__styled__/StyledBasketTable';
import BasketClearItems from '/business/shared/components/widgets/basket-table/BasketClearItems';

// import StyledConsumptionTable from './__styled__/StyledConsumptionTable';

const addPageLink = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/consumption-data/consumption-data-add`;

interface Tab {
  id: ElectricityHeatSteamConsumptionData['flag_type'] | null;
  labelKey: string;
}

const tabs: Tab[] = [
  {
    id: null,
    labelKey: 'questions:electricityHeatSteam.components.consumptionTable.tabs.summary',
  },
  {
    id: 'electricity',
    labelKey: 'questions:electricityHeatSteam.components.consumptionTable.tabs.electricity',
  },
  {
    id: 'heating',
    labelKey: 'questions:electricityHeatSteam.components.consumptionTable.tabs.heat',
  },
  {
    id: 'steam',
    labelKey: 'questions:electricityHeatSteam.components.consumptionTable.tabs.steam',
  },
];

function LocationRow({
  location,
  currentTabId,
}: {
  location: ElectricityHeatSteamLocationDecorated;
  currentTabId: Tab['id'];
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();

  const handleRemove = useCallback(
    async (items: ElectricityHeatSteamConsumptionData[]) => {
      // @todo: Typescript for redux
      await Promise.all(
        items.map((item) =>
          dispatch(
            saveConsumptionData({
              basketItem: {
                id: item.id,
                genericData: item,
              },
              applicationId: appId ?? 0,
              isDelete: true,
            }),
          ),
        ),
      );
    },
    [dispatch, appId],
  );
  // Show gross consumption (pre-certificate), matching the backend's locationAdjustedKwh
  const spaceRatio = location.spaceRatio || 1;
  const kwhAggregated = location.attachedConsumptionData
    .filter((data) => !currentTabId || data.flag_type === currentTabId)
    .reduce(
      (sum, data) => sum + ((data.activity_quantity_kwh || 0) + (data.supplier_quantity_kwh || 0)) * spaceRatio,
      0,
    );
  let tCO2eAggregated = 0;
  if (currentTabId === null) {
    tCO2eAggregated = location?.aggregated_tonnage || 0;
  } else if (location?.aggregated_data) {
    tCO2eAggregated = location?.aggregated_data[currentTabId].tonnage || 0;
  }

  const matchedConsumption = location?.attachedConsumptionData.find((data) => data.flag_type === currentTabId);
  return (
    <tr key={`location-${location.id}`}>
      <td>{location.unique_name}</td>
      <td>
        {formatTonnage(kwhAggregated)} {t('business:emissionFactors.electricity.kwh')}
      </td>
      <td>
        {formatTonnage(tCO2eAggregated)} {t('business:emissionFactors.emissionUnits.tonnageRaw')}
      </td>
      {location.attachedConsumptionData && location.attachedConsumptionData.length > 0 && matchedConsumption ? (
        <>
          <td>
            <LinkButton $secondary to={getUrlWithAppId(`${addPageLink}?edit=${matchedConsumption.id}`, appId)}>
              {isEditable
                ? t('questions:electricityHeatSteam.shared.edit')
                : t('questions:electricityHeatSteam.shared.view')}
            </LinkButton>
          </td>
          <td>
            <Button disabled={!isEditable} $secondary onClick={() => handleRemove([matchedConsumption])}>
              {t('questions:electricityHeatSteam.shared.remove')}
            </Button>
          </td>
        </>
      ) : (
        <>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </>
      )}
    </tr>
  );
}

function ConsumptionTable() {
  const { t } = useTranslation();
  const locations = useSelector(getElectDecoratedLocationsSelector);
  const numLocationsInputted = useSelector(getElectNumLocationsInputtedSelector);
  const dispatch = useDispatch<AppDispatch>();
  const [currentTabId, setCurrentTabId] = useState<Tab['id']>(null);
  const currentTab = tabs.find(({ id }) => id === currentTabId);
  const appId = useSelector(getApplicationIdSelector);
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const locationsFiltered = locations.filter((location) => {
    return (
      location.attachedConsumptionData?.some((data) => {
        return data.flag_type === currentTabId || !currentTabId;
      }) ?? false
    );
  });

  // @todo: Should this go into a selector or is that overkill?
  const totalCarbonEmissions = locationsFiltered.reduce((total, location) => {
    if (location?.aggregated_data && currentTabId) {
      const tCO2eAggregated = location?.aggregated_data[currentTabId].tonnage || 0;
      return total + tCO2eAggregated;
    }
    return total + (location.aggregated_tonnage || 0);
  }, 0);

  const handleRemoveBulk = useCallback(
    () =>
      locationsFiltered.map((item) => {
        const matchedConsumption = item?.attachedConsumptionData.find((data) => data.flag_type === currentTabId);
        if (matchedConsumption) {
          dispatch(
            saveConsumptionData({
              basketItem: {
                id: matchedConsumption.id,
                genericData: matchedConsumption,
              },
              applicationId: appId ?? 0,
              isDelete: true,
            }),
          );
        }
      }),
    [dispatch, appId, currentTabId],
  );
  // {/* @todo: Use proper table element */}
  return (
    <div>
      <StyledTabBar>
        {tabs.map((tab) => (
          <StyledTabBarItem key={tab.id ?? 'summary'} $isActive={currentTabId === tab.id}>
            <button type="button" onClick={() => setCurrentTabId(tab.id)}>
              {t(tab.labelKey)}
            </button>
          </StyledTabBarItem>
        ))}
      </StyledTabBar>
      <StyledHeading as="h3" $type="h3">
        {currentTabId === null ? (
          <>
            {/* @i18n: Total number of locations with consumption data: 1 */}
            <Trans
              i18nKey="questions:electricityHeatSteam.components.consumptionTable.totalLocationsWithData"
              values={{ numLocationsInputted }}
              components={{ 1: <strong /> }}
            />
          </>
        ) : (
          <>
            <span>
              {t('questions:electricityHeatSteam.components.consumptionTable.calculatedFor', {
                label: currentTab ? t(currentTab.labelKey).toLowerCase() : '',
              })}
            </span>
            <strong>
              {formatTonnage(totalCarbonEmissions)} {t('business:emissionFactors.emissionUnits.tonnageRaw')}
            </strong>
          </>
        )}
      </StyledHeading>
      {locationsFiltered.length ? (
        <>
          {currentTabId !== null && isEditable && <BasketClearItems deleteCallbackButton={handleRemoveBulk} />}
          <StyledBasketTable>
            <thead>
              <tr>
                <th>{t('questions:electricityHeatSteam.locations.table.locationName')}</th>
                <th>{t('questions:electricityHeatSteam.components.consumptionTable.kwhUsage')}</th>
                <th>{t('questions:electricityHeatSteam.locations.table.carbonEmissions')}</th>
                <th>&nbsp;</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {locationsFiltered?.map((location) => (
                <LocationRow key={`location-${location.id}`} location={location} currentTabId={currentTabId} />
              ))}
            </tbody>
          </StyledBasketTable>
        </>
      ) : (
        <div>{t('questions:electricityHeatSteam.components.consumptionTable.noConsumptionData')}</div>
      )}
    </div>
  );
}

export default ConsumptionTable;
